import { trackAction } from '@lessonup/client-integration';
import { EduSystem } from '@lessonup/teaching-core';
import { FacetKey, FacetResult } from '@lessonup/typescript-core/domain';
import { SearchSidebarViewProps, SearchSidebarView, SearchType, Option } from '@lessonup/typescript-core/ui';
import { get } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchContext } from './SearchContext';
import './SearchSidebar.less';

const FALLBACK_COUNTRY = 'nl';

interface Props {
  searchTextValue?: string;
  facetResults: FacetResult[];
  hideFacets?: FacetKey[];
  hideText?: boolean;
  hideCounts?: boolean;
  searchType: SearchType;
}

export default function SearchSidebar({
  facetResults,
  searchTextValue,
  hideFacets,
  hideText,
  hideCounts,
  searchType,
}: Props) {
  const { selectedFacets, resetFacet, setFacet, setSearchText, selectedText, currentParams } = useSearchContext();
  const visibleFacets = selectedFacets;

  const isMulti = currentParams().isMulti;

  const selectedCountry = get(
    visibleFacets.find((facet) => facet.key === 'country'),
    'value',
    [FALLBACK_COUNTRY]
  )[0];

  const selectedSchoolType = get(
    visibleFacets.find((facet) => facet.key === 'schoolType'),
    'value'
  )?.[0];

  const eduSystem = useMemo(() => EduSystem.get(selectedCountry), [selectedCountry]);

  const [searchTextState, setSearchTextState] = useState<string | undefined>(searchTextValue || selectedText);

  useEffect(() => {
    if (!selectedText) {
      setSearchTextState('');
    }
  }, [selectedText]);

  const handleSearchSubmit = useCallback(() => {
    setSearchText(searchTextState);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-generated TECH-258
    const action = ('search-' + searchType) as any; // TODO: will work better in TS 4.1
    trackAction(action, { text: searchTextState });
  }, [searchTextState, searchType, setSearchText]);

  const handleSearchChange = useCallback(
    (s: string) => {
      setSearchTextState(s);

      if (s === '') {
        setSearchText(undefined);
      }
    },
    [setSearchText]
  );

  const handleFacetChange = useCallback(
    (facetKey: FacetKey) => {
      return (option?: Option | Option[]) => {
        if (!option) {
          resetFacet(facetKey);
          return;
        }

        setFacet(facetKey, Array.isArray(option) ? option.map(({ value }) => value) : [option.value]);
      };
    },
    [resetFacet, setFacet]
  );

  const handleCheckboxFacetChange = useCallback(
    (facetKey: FacetKey) => (checked: boolean) => (checked ? setFacet(facetKey, ['true']) : resetFacet(facetKey)),
    [resetFacet, setFacet]
  );

  const facetCheckboxIsChecked = useCallback(
    (facetKey: FacetKey) => selectedFacets.some((f) => f.key === facetKey && f.value?.[0] === 'true'),
    [selectedFacets]
  );

  const viewModel: SearchSidebarViewProps = {
    facetResults,
    textSearch: {
      searchText: searchTextState,
      handleSearchTextChange: handleSearchChange,
      handleSearchTextSubmit: handleSearchSubmit,
    },
    facetSelect: {
      selectedSchoolType,
      selectedFacets: visibleFacets,
      eduSystem,
      selectedCountry,
      handleFacetChange,
      isMulti,
    },
    checkbox: {
      facetCheckboxIsChecked,
      handleCheckboxFacetChange,
    },
    hideFacets,
    hideText,
    hideCounts,
    searchType,
  };

  return <SearchSidebarView {...viewModel} />;
}
